import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { useCourse } from '../../hooks/useCourses';
import { useAdminCourses } from '../../hooks/useAdminCourses';
import { useFileUpload } from '../../hooks/useFileUpload';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import AdminBreadcrumb from '../../components/admin/AdminBreadcrumb';
import ModuleManager from '../../components/admin/ModuleManager';
import SafeIcon from '../../common/SafeIcon';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';

const { FiSave, FiArrowLeft, FiUpload, FiX, FiPlus, FiTrash2, FiCalendar } = FiIcons;

const CourseEditor = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const isNew = courseId === 'new';
  const { course, modules, loading } = useCourse(courseId, { enabled: !isNew });
  const { createCourse, updateCourse } = useAdminCourses();
  const { uploadFile, uploading } = useFileUpload();
  
  const [coverImagePreview, setCoverImagePreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [syllabus, setSyllabus] = useState([]);

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      title: '',
      slug: '',
      summary: '',
      description: '',
      price: 0,
      is_published: false,
      is_coming_soon: false,
      cover_image_url: ''
    }
  });

  const watchTitle = watch('title');
  const watchIsComingSoon = watch('is_coming_soon');

  // Auto-generate slug from title
  useEffect(() => {
    if (watchTitle && isNew) {
      const slug = watchTitle
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .trim();
      setValue('slug', slug);
    }
  }, [watchTitle, setValue, isNew]);

  // Load course data for editing
  useEffect(() => {
    if (course && !isNew) {
      setValue('title', course.title);
      setValue('slug', course.slug);
      setValue('summary', course.summary || '');
      setValue('description', course.description || '');
      setValue('price', course.price);
      setValue('is_published', course.is_published);
      setValue('is_coming_soon', course.is_coming_soon || false);
      setValue('cover_image_url', course.cover_image_url || '');
      setCoverImagePreview(course.cover_image_url || '');
      setSyllabus(course.syllabus || []);
    }
  }, [course, setValue, isNew]);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const path = `courses/${Date.now()}-${file.name}`;
      const uploadedFile = await uploadFile(file, path);
      const imageUrl = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/course-resources/${uploadedFile.path}`;
      setValue('cover_image_url', imageUrl);
      setCoverImagePreview(imageUrl);
      toast.success('Image uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload image');
    }
  };

  const addSyllabusSection = () => {
    setSyllabus(prev => [...prev, {
      title: '',
      description: '',
      lessons: ['']
    }]);
  };

  const updateSyllabusSection = (index, field, value) => {
    setSyllabus(prev => prev.map((section, i) => 
      i === index ? { ...section, [field]: value } : section
    ));
  };

  const addLessonToSection = (sectionIndex) => {
    setSyllabus(prev => prev.map((section, i) => 
      i === sectionIndex 
        ? { ...section, lessons: [...section.lessons, ''] }
        : section
    ));
  };

  const updateSyllabusLesson = (sectionIndex, lessonIndex, value) => {
    setSyllabus(prev => prev.map((section, i) => 
      i === sectionIndex 
        ? { 
            ...section, 
            lessons: section.lessons.map((lesson, j) => 
              j === lessonIndex ? value : lesson
            )
          }
        : section
    ));
  };

  const removeSyllabusLesson = (sectionIndex, lessonIndex) => {
    setSyllabus(prev => prev.map((section, i) => 
      i === sectionIndex 
        ? { 
            ...section, 
            lessons: section.lessons.filter((_, j) => j !== lessonIndex)
          }
        : section
    ));
  };

  const removeSyllabusSection = (index) => {
    setSyllabus(prev => prev.filter((_, i) => i !== index));
  };

  const onSubmit = async (data) => {
    setSaving(true);
    
    try {
      // Clean up syllabus data
      const cleanSyllabus = syllabus
        .filter(section => section.title.trim())
        .map(section => ({
          ...section,
          lessons: section.lessons.filter(lesson => lesson.trim())
        }));

      const courseData = {
        ...data,
        syllabus: cleanSyllabus
      };

      if (isNew) {
        const { data: newCourse } = await createCourse(courseData);
        if (newCourse) {
          toast.success('Course created successfully');
          navigate(`/admin/courses/${newCourse.id}`);
        }
      } else {
        await updateCourse(courseId, courseData);
        toast.success('Course updated successfully');
      }
    } catch (error) {
      toast.error(isNew ? 'Failed to create course' : 'Failed to update course');
    } finally {
      setSaving(false);
    }
  };

  const breadcrumbItems = [
    { label: 'Admin', href: '/admin' },
    { label: 'Courses', href: '/admin/courses' },
    { label: isNew ? 'New Course' : course?.title || 'Loading...', href: '#' }
  ];

  if (loading && !isNew) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <AdminBreadcrumb items={breadcrumbItems} />

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin/courses')}
              className="p-2 rounded-md text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {isNew ? 'Create New Course' : `Edit ${course?.title}`}
              </h1>
              <p className="text-gray-600 mt-1">
                {isNew ? 'Create a new course for your students' : 'Modify course details and content'}
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Course Details Form */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg border border-gray-200 p-6"
            >
              <h2 className="text-xl font-bold text-gray-900 mb-6">Course Details</h2>
              
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course Title *
                  </label>
                  <input
                    {...register('title', { required: 'Title is required' })}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                    placeholder="Enter course title"
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                  )}
                </div>

                {/* Slug */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL Slug *
                  </label>
                  <input
                    {...register('slug', { required: 'Slug is required' })}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                    placeholder="course-url-slug"
                  />
                  {errors.slug && (
                    <p className="mt-1 text-sm text-red-600">{errors.slug.message}</p>
                  )}
                </div>

                {/* Summary */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course Summary
                  </label>
                  <textarea
                    {...register('summary')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                    placeholder="Brief description of the course..."
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Course Description
                  </label>
                  <textarea
                    {...register('description')}
                    rows={5}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                    placeholder="Detailed description of the course content, objectives, and what students will learn..."
                  />
                </div>

                {/* Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price ($)
                  </label>
                  <input
                    {...register('price', {
                      valueAsNumber: true,
                      min: { value: 0, message: 'Price cannot be negative' }
                    })}
                    type="number"
                    step="0.01"
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-nutrition-500 focus:border-nutrition-500"
                    placeholder="0.00"
                  />
                  {errors.price && (
                    <p className="mt-1 text-sm text-red-600">{errors.price.message}</p>
                  )}
                </div>

                {/* Cover Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cover Image
                  </label>
                  <div className="space-y-4">
                    {coverImagePreview && (
                      <div className="relative">
                        <img
                          src={coverImagePreview}
                          alt="Cover preview"
                          className="w-full h-48 object-cover rounded-md border border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setCoverImagePreview('');
                            setValue('cover_image_url', '');
                          }}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        >
                          <SafeIcon icon={FiX} className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    <div className="flex items-center justify-center w-full">
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <SafeIcon icon={FiUpload} className="w-8 h-8 mb-4 text-gray-500" />
                          <p className="mb-2 text-sm text-gray-500">
                            <span className="font-semibold">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-gray-500">PNG, JPG or WEBP (MAX. 2MB)</p>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept="image/*"
                          onChange={handleImageUpload}
                          disabled={uploading}
                        />
                      </label>
                    </div>
                    {uploading && (
                      <div className="flex items-center justify-center py-2">
                        <LoadingSpinner size="sm" />
                        <span className="ml-2 text-sm text-gray-600">Uploading...</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Course Status */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <input
                      {...register('is_coming_soon')}
                      type="checkbox"
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <label className="text-sm font-medium text-gray-700 flex items-center space-x-2">
                      <SafeIcon icon={FiCalendar} className="w-4 h-4 text-purple-600" />
                      <span>Coming Soon (show waitlist instead of enrollment)</span>
                    </label>
                  </div>
                  
                  {!watchIsComingSoon && (
                    <div className="flex items-center space-x-3">
                      <input
                        {...register('is_published')}
                        type="checkbox"
                        className="h-4 w-4 text-nutrition-600 focus:ring-nutrition-500 border-gray-300 rounded"
                      />
                      <label className="text-sm font-medium text-gray-700">
                        Publish course (make visible to students)
                      </label>
                    </div>
                  )}
                </div>

                {/* Syllabus for Coming Soon Courses */}
                {watchIsComingSoon && (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Course Syllabus/Outline
                      </label>
                      <button
                        type="button"
                        onClick={addSyllabusSection}
                        className="flex items-center space-x-1 text-purple-600 hover:text-purple-700 text-sm font-medium"
                      >
                        <SafeIcon icon={FiPlus} className="w-4 h-4" />
                        <span>Add Section</span>
                      </button>
                    </div>
                    
                    <div className="space-y-4">
                      {syllabus.map((section, sectionIndex) => (
                        <div key={sectionIndex} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-medium text-gray-900">Section {sectionIndex + 1}</h4>
                            <button
                              type="button"
                              onClick={() => removeSyllabusSection(sectionIndex)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                          </div>
                          
                          <div className="space-y-3">
                            <input
                              type="text"
                              value={section.title}
                              onChange={(e) => updateSyllabusSection(sectionIndex, 'title', e.target.value)}
                              placeholder="Section title..."
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            />
                            
                            <textarea
                              value={section.description}
                              onChange={(e) => updateSyllabusSection(sectionIndex, 'description', e.target.value)}
                              placeholder="Section description..."
                              rows={2}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                            />
                            
                            <div>
                              <div className="flex items-center justify-between mb-2">
                                <label className="text-sm font-medium text-gray-700">Planned Lessons</label>
                                <button
                                  type="button"
                                  onClick={() => addLessonToSection(sectionIndex)}
                                  className="text-purple-600 hover:text-purple-700 text-sm"
                                >
                                  <SafeIcon icon={FiPlus} className="w-3 h-3" />
                                </button>
                              </div>
                              
                              <div className="space-y-2">
                                {section.lessons.map((lesson, lessonIndex) => (
                                  <div key={lessonIndex} className="flex items-center space-x-2">
                                    <input
                                      type="text"
                                      value={lesson}
                                      onChange={(e) => updateSyllabusLesson(sectionIndex, lessonIndex, e.target.value)}
                                      placeholder={`Lesson ${lessonIndex + 1} title...`}
                                      className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => removeSyllabusLesson(sectionIndex, lessonIndex)}
                                      className="text-red-600 hover:text-red-700"
                                    >
                                      <SafeIcon icon={FiX} className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                      
                      {syllabus.length === 0 && (
                        <div className="text-center py-8 text-gray-500">
                          <p className="mb-4">No syllabus sections yet</p>
                          <button
                            type="button"
                            onClick={addSyllabusSection}
                            className="text-purple-600 hover:text-purple-700 font-medium"
                          >
                            Add your first section
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={saving || uploading}
                    className="w-full bg-nutrition-600 hover:bg-nutrition-700 text-white py-3 px-4 rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {saving ? (
                      <LoadingSpinner size="sm" />
                    ) : (
                      <SafeIcon icon={FiSave} className="w-4 h-4" />
                    )}
                    <span>
                      {saving ? 'Saving...' : isNew ? 'Create Course' : 'Update Course'}
                    </span>
                  </button>
                </div>
              </form>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Course Actions */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-lg border border-gray-200 p-6"
            >
              <h3 className="text-lg font-bold text-gray-900 mb-4">Course Actions</h3>
              <div className="space-y-3">
                {!isNew && (
                  <>
                    <button
                      onClick={() => navigate(`/courses/${course?.slug}`)}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                    >
                      Preview Course
                    </button>
                    {!course?.is_coming_soon && (
                      <button
                        onClick={() => navigate(`/admin/courses/${courseId}/bulk-import`)}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                      >
                        Bulk Import Lessons
                      </button>
                    )}
                  </>
                )}
              </div>
            </motion.div>

            {/* Course Stats */}
            {!isNew && course && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-lg border border-gray-200 p-6"
              >
                <h3 className="text-lg font-bold text-gray-900 mb-4">Course Stats</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Type:</span>
                    <span className={`font-medium ${course.is_coming_soon ? 'text-purple-600' : 'text-gray-900'}`}>
                      {course.is_coming_soon ? 'Coming Soon' : 'Regular Course'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Modules:</span>
                    <span className="font-medium">{modules?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Lessons:</span>
                    <span className="font-medium">
                      {modules?.reduce((acc, module) => acc + (module.lessons?.length || 0), 0) || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className={`font-medium ${
                      course.is_coming_soon 
                        ? 'text-purple-600' 
                        : course.is_published 
                          ? 'text-green-600' 
                          : 'text-gray-600'
                    }`}>
                      {course.is_coming_soon 
                        ? 'Coming Soon' 
                        : course.is_published 
                          ? 'Published' 
                          : 'Draft'
                      }
                    </span>
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Module Management - Only show for regular courses */}
        {!isNew && course && !course.is_coming_soon && (
          <div className="mt-8">
            <ModuleManager courseId={courseId} modules={modules} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseEditor;